import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesVehiculosAPI, ordenesClientesAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { ColumnSelector } from '../../components/ui/column-selector';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Car,
    Plus,
    Search,
    ArrowLeft,
    Eye,
    Edit,
    Trash2,
    Loader2,
} from 'lucide-react';

// Definición de columnas disponibles
const VEHICLE_COLUMNS = [
    { key: 'patente', label: 'Patente', required: true, defaultVisible: true },
    { key: 'imei', label: 'IMEI', defaultVisible: true },
    { key: 'cliente', label: 'Cliente', defaultVisible: true },
    { key: 'rut', label: 'RUT Cliente', defaultVisible: true },
    { key: 'tipo', label: 'Tipo Vehículo', defaultVisible: true },
    { key: 'marca', label: 'Marca', defaultVisible: true },
    { key: 'modelo', label: 'Modelo', defaultVisible: true },
    { key: 'anio', label: 'Año', defaultVisible: false },
    { key: 'vin', label: 'VIN/Chasis', defaultVisible: false },
    { key: 'gps_marca', label: 'GPS Marca', defaultVisible: false },
    { key: 'gps_modelo', label: 'GPS Modelo', defaultVisible: false },
    { key: 'transmision', label: 'Transmisión', defaultVisible: false },
    { key: 'fecha_instalacion', label: 'Fecha Instalación', defaultVisible: false },
    { key: 'grupo', label: 'Grupo', defaultVisible: false },
];

const TIPOS_VEHICULO = [
    { codigo: 'camion', nombre: 'Camión' },
    { codigo: 'camioneta', nombre: 'Camioneta' },
    { codigo: 'bus', nombre: 'Bus' },
    { codigo: 'minibus', nombre: 'Minibús' },
    { codigo: 'automovil', nombre: 'Automóvil' },
    { codigo: 'maquinaria', nombre: 'Maquinaria' },
    { codigo: 'otro', nombre: 'Otro' },
];

const initialFormState = {
    cliente_id: '',
    patente: '',
    tipo_vehiculo: '',
    marca: '',
    modelo: '',
    anio: '',
    color: '',
    vin: '',
};

export default function OrdenesVehiculosPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [vehiculos, setVehiculos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [search, setSearch] = useState('');
    const [clienteFilter, setClienteFilter] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    
    // Selección múltiple
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Columnas visibles
    const [visibleColumns, setVisibleColumns] = useState(
        VEHICLE_COLUMNS.filter(c => c.defaultVisible !== false).map(c => c.key)
    );

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadVehiculos();
    }, [clienteFilter]);

    const loadInitialData = async () => {
        try {
            const clientesRes = await ordenesClientesAPI.getAll();
            setClientes(clientesRes.data);
        } catch (err) {
            console.error('Error loading clientes:', err);
        }
    };

    const loadVehiculos = async () => {
        try {
            setLoading(true);
            const params = {};
            if (clienteFilter && clienteFilter !== '_all') params.cliente_id = clienteFilter;
            const res = await ordenesVehiculosAPI.getAll(params);
            setVehiculos(res.data);
            setSelectedIds([]); // Limpiar selección al recargar
        } catch (err) {
            console.error('Error loading vehiculos:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingItem(null);
        setFormData(initialFormState);
        setShowDialog(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            cliente_id: item.cliente_id || '',
            patente: item.patente || '',
            tipo_vehiculo: item.tipo_vehiculo || '',
            marca: item.marca || '',
            modelo: item.modelo || '',
            anio: item.anio?.toString() || '',
            color: item.color || '',
            vin: item.vin || '',
        });
        setShowDialog(true);
    };

    const handleOpenDelete = (item) => {
        setEditingItem(item);
        setShowDeleteDialog(true);
    };

    const handleSave = async () => {
        if (!formData.cliente_id || !formData.patente) {
            toast.error('Cliente y Patente son requeridos');
            return;
        }

        setSaving(true);
        try {
            const dataToSend = {
                ...formData,
                anio: formData.anio ? parseInt(formData.anio) : null,
            };
            
            if (editingItem) {
                await ordenesVehiculosAPI.update(editingItem.id, dataToSend);
                toast.success('Vehículo actualizado');
            } else {
                await ordenesVehiculosAPI.create(dataToSend);
                toast.success('Vehículo creado');
            }
            setShowDialog(false);
            loadVehiculos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await ordenesVehiculosAPI.delete(editingItem.id);
            toast.success('Vehículo eliminado');
            setShowDeleteDialog(false);
            loadVehiculos();
        } catch (err) {
            toast.error('Error al eliminar vehículo');
        } finally {
            setSaving(false);
        }
    };

    // Selección múltiple
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(filteredVehiculos.map(v => v.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(i => i !== id));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        
        setSaving(true);
        try {
            const res = await ordenesVehiculosAPI.deleteBulk(selectedIds);
            toast.success(res.data.message);
            setShowBulkDeleteDialog(false);
            setSelectedIds([]);
            loadVehiculos();
        } catch (err) {
            toast.error('Error al eliminar vehículos');
        } finally {
            setSaving(false);
        }
    };

    const filteredVehiculos = vehiculos.filter(v => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            v.patente?.toLowerCase().includes(searchLower) ||
            v.marca?.toLowerCase().includes(searchLower) ||
            v.modelo?.toLowerCase().includes(searchLower) ||
            v.cliente?.razon_social?.toLowerCase().includes(searchLower)
        );
    });

    const allSelected = filteredVehiculos.length > 0 && selectedIds.length === filteredVehiculos.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < filteredVehiculos.length;

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Car className="h-6 w-6" />
                            Vehículos
                        </h1>
                        <p className="text-muted-foreground">Gestión de vehículos de clientes</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {selectedIds.length > 0 && tienePermiso('eliminar_vehiculos') && (
                        <Button 
                            variant="destructive" 
                            onClick={() => setShowBulkDeleteDialog(true)}
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar ({selectedIds.length})
                        </Button>
                    )}
                    {tienePermiso('crear_vehiculos') && (
                        <Button onClick={handleOpenCreate} data-testid="add-vehicle-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Agregar Vehículo
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por patente, marca, modelo, cliente..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                    />
                </div>
                
                <Select value={clienteFilter || "_all"} onValueChange={(v) => setClienteFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filtrar por cliente" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos los clientes</SelectItem>
                        {clientes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <ColumnSelector
                    columns={VEHICLE_COLUMNS}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={setVisibleColumns}
                    storageKey="vehiculos-columns"
                />
            </div>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : filteredVehiculos.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Car className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay vehículos registrados</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">
                                        <Checkbox 
                                            checked={allSelected}
                                            ref={(el) => el && (el.indeterminate = someSelected)}
                                            onCheckedChange={handleSelectAll}
                                            data-testid="select-all-checkbox"
                                        />
                                    </TableHead>
                                    {visibleColumns.includes('patente') && <TableHead>Patente</TableHead>}
                                    {visibleColumns.includes('imei') && <TableHead>IMEI</TableHead>}
                                    {visibleColumns.includes('cliente') && <TableHead>Cliente</TableHead>}
                                    {visibleColumns.includes('rut') && <TableHead>RUT</TableHead>}
                                    {visibleColumns.includes('tipo') && <TableHead>Tipo Vehículo</TableHead>}
                                    {visibleColumns.includes('marca') && <TableHead>Marca</TableHead>}
                                    {visibleColumns.includes('modelo') && <TableHead>Modelo</TableHead>}
                                    {visibleColumns.includes('anio') && <TableHead>Año</TableHead>}
                                    {visibleColumns.includes('vin') && <TableHead>VIN/Chasis</TableHead>}
                                    {visibleColumns.includes('gps_marca') && <TableHead>GPS Marca</TableHead>}
                                    {visibleColumns.includes('gps_modelo') && <TableHead>GPS Modelo</TableHead>}
                                    {visibleColumns.includes('transmision') && <TableHead>Transmisión</TableHead>}
                                    {visibleColumns.includes('fecha_instalacion') && <TableHead>Fecha Instalación</TableHead>}
                                    {visibleColumns.includes('grupo') && <TableHead>Grupo</TableHead>}
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredVehiculos.map((item) => (
                                    <TableRow key={item.id} data-testid={`vehicle-${item.id}`}>
                                        <TableCell>
                                            <Checkbox 
                                                checked={selectedIds.includes(item.id)}
                                                onCheckedChange={(checked) => handleSelectOne(item.id, checked)}
                                            />
                                        </TableCell>
                                        {visibleColumns.includes('patente') && (
                                            <TableCell className="font-mono font-bold">{item.patente}</TableCell>
                                        )}
                                        {visibleColumns.includes('imei') && (
                                            <TableCell className="font-mono text-xs">{item.foursat_imei || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('cliente') && (
                                            <TableCell>
                                                <span className="truncate max-w-[150px] block">{item.cliente?.razon_social || item.cliente_razon_social || '-'}</span>
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('rut') && (
                                            <TableCell className="font-mono text-xs">{item.cliente?.rut || item.cliente_rut || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('tipo') && (
                                            <TableCell>{TIPOS_VEHICULO.find(t => t.codigo === item.tipo_vehiculo)?.nombre || item.tipo_vehiculo || item.tipo || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('marca') && (
                                            <TableCell>{item.marca || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('modelo') && (
                                            <TableCell>{item.modelo || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('anio') && (
                                            <TableCell>{item.anio || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('vin') && (
                                            <TableCell className="font-mono text-xs">{item.vin || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('gps_marca') && (
                                            <TableCell>{item.gps_marca || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('gps_modelo') && (
                                            <TableCell>{item.gps_modelo || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('transmision') && (
                                            <TableCell>{item.gps_frecuencia_seg ? `${item.gps_frecuencia_seg}s` : '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('fecha_instalacion') && (
                                            <TableCell>{item.fecha_instalacion?.split('T')[0] || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('grupo') && (
                                            <TableCell>{item.foursat_group || '-'}</TableCell>
                                        )}
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => navigate(`/ordenes/vehiculos/${item.id}`)}
                                                    title="Ver detalle"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {tienePermiso('editar_vehiculos') && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {tienePermiso('eliminar_vehiculos') && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(item)}>
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Summary */}
            <div className="text-sm text-muted-foreground text-center">
                {selectedIds.length > 0 ? (
                    <span className="text-primary font-medium">{selectedIds.length} seleccionados de </span>
                ) : null}
                {filteredVehiculos.length} vehículos registrados
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Editar Vehículo' : 'Agregar Vehículo'}</DialogTitle>
                        <DialogDescription>
                            {editingItem ? 'Modifica los datos del vehículo' : 'Ingresa los datos del nuevo vehículo'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Cliente *</Label>
                            <Select value={formData.cliente_id} onValueChange={(v) => setFormData({...formData, cliente_id: v})}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clientes.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Patente *</Label>
                                <Input
                                    value={formData.patente}
                                    onChange={(e) => setFormData({...formData, patente: e.target.value.toUpperCase()})}
                                    placeholder="Ej: ABCD-12"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Tipo de Vehículo</Label>
                                <Select value={formData.tipo_vehiculo || "_none"} onValueChange={(v) => setFormData({...formData, tipo_vehiculo: v === "_none" ? "" : v})}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">Sin especificar</SelectItem>
                                        {TIPOS_VEHICULO.map(t => (
                                            <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Marca</Label>
                                <Input
                                    value={formData.marca}
                                    onChange={(e) => setFormData({...formData, marca: e.target.value})}
                                    placeholder="Ej: Toyota"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Modelo</Label>
                                <Input
                                    value={formData.modelo}
                                    onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                                    placeholder="Ej: Hilux"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Año</Label>
                                <Input
                                    type="number"
                                    value={formData.anio}
                                    onChange={(e) => setFormData({...formData, anio: e.target.value})}
                                    placeholder="2024"
                                    min="1900"
                                    max="2030"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <Input
                                    value={formData.color}
                                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                                    placeholder="Ej: Blanco"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>VIN</Label>
                                <Input
                                    value={formData.vin}
                                    onChange={(e) => setFormData({...formData, vin: e.target.value.toUpperCase()})}
                                    placeholder="Chasis"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editingItem ? 'Guardar' : 'Crear'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Vehículo</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar el vehículo <strong>{editingItem?.patente}</strong>? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Delete Confirmation */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Vehículos</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar <strong>{selectedIds.length} vehículos</strong>? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleBulkDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Eliminar {selectedIds.length} vehículos
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
